/**
 * TanStack Query bindings for the contract.
 *
 * Query keys are built here rather than inline in screens, so an invalidation
 * after a write reaches every view that shows the thing that changed — the
 * detail screen, the list behind it and the dashboard KPI row.
 *
 * `useDeca` returns the `ETag` alongside the document. Every operation that
 * mutates one existing DeCA requires `If-Match`, and only `GET /decas/{id}`
 * hands out an `ETag`, so the read has to carry its version forward into the
 * write. Without that, two traffic managers editing the same document silently
 * overwrite one another — on a legal record, the failure that matters.
 */

import { keepPreviousData, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { unwrap, unwrapWithETag, type paths, type User } from '@deca/api'

import { SUGGESTION_MIN_LENGTH, type AddressBookKind } from './addressBook.ts'
import { decaEmailPollInterval } from '../deca/decaEmails.ts'
import { isEmailAddress } from '../deca/emailAddress.ts'
import { api, fetchBinary, MIME } from './api.ts'

type Query<P extends keyof paths> = paths[P] extends { get: { parameters: { query?: infer Q } } }
  ? NonNullable<Q>
  : never

export type DecaListQuery = Query<'/api/v1/decas'>
export type PassengerDecaListQuery = Query<'/api/v1/passenger-decas'>
export type DecaStatsQuery = Query<'/api/v1/decas/stats'>
export type AuditQuery = Query<'/api/v1/audit-events'>
export type UserQuery = Query<'/api/v1/users'>
export type OrganisationQuery = Query<'/api/v1/organisations'>
export type ExternalDecaQuery = Query<'/api/v1/external-decas'>
export type AddressBookQuery = Query<'/api/v1/address-book'>
export type ImportRowStatus = 'VALID' | 'ERROR' | 'CREATED' | 'SKIPPED'

export const qk = {
  decas: (query: DecaListQuery = {}) => ['decas', query] as const,
  passengerDecas: (query: PassengerDecaListQuery = {}) => ['passenger-decas', query] as const,
  deca: (id: string) => ['deca', id] as const,
  decaHistory: (id: string) => ['deca', id, 'history'] as const,
  decaVersions: (id: string) => ['deca', id, 'versions'] as const,
  // Under the document's prefix, so a write to it (a cancellation) reads the list again. The
  // photographs are not: they never change, and every read of one is an audit row on the server.
  deliveryConfirmations: (id: string) => ['deca', id, 'delivery-confirmations'] as const,
  // #219: under the document's prefix too, so a write to it — a sending among them — reads it again.
  decaEmails: (id: string) => ['deca', id, 'emails'] as const,
  // Sorted and in lower case, so the same addresses in another order are one question.
  emailAddressCheck: (addresses: readonly string[]) => ['email-address-check', ...addresses] as const,
  deliveryPhoto: (decaId: string, confirmationId: string, n: number) =>
    ['delivery-photo', decaId, confirmationId, n] as const,
  // `passenger-deca` singular, so the per-sheet keys sit outside the `passenger-decas`
  // list prefix exactly as the goods pair does. Invalidating one never sweeps the other
  // by accident, and both are swept deliberately by `invalidatePassengerDeca`.
  passengerDeca: (id: string) => ['passenger-deca', id] as const,
  passengerDecaHistory: (id: string) => ['passenger-deca', id, 'history'] as const,
  passengerDecaVersions: (id: string) => ['passenger-deca', id, 'versions'] as const,
  decaStats: (query: DecaStatsQuery = {}) => ['deca-stats', query] as const,
  imports: () => ['imports'] as const,
  import: (id: string) => ['import', id] as const,
  importRows: (id: string, status?: ImportRowStatus) => ['import', id, 'rows', status ?? 'all'] as const,
  organisations: (query: OrganisationQuery = {}) => ['organisations', query] as const,
  organisation: (id: string) => ['organisation', id] as const,
  users: (query: UserQuery = {}) => ['users', query] as const,
  apiKeys: () => ['api-keys'] as const,
  serviceAccounts: () => ['service-accounts'] as const,
  // The organisation is in these two keys, unlike the rest: it is in the path, and a key without it
  // would serve one company's phones under another's if the id ever changed under a mounted screen.
  phoneLinks: (orgId: string) => ['phone-links', orgId] as const,
  phoneLinkCodes: (orgId: string) => ['phone-link-codes', orgId] as const,
  audit: (query: AuditQuery = {}) => ['audit-events', query] as const,
  externalDecas: (query: ExternalDecaQuery = {}) => ['external-decas', query] as const,
  externalDeca: (id: string) => ['external-deca', id] as const,
  // Two prefixes, one per reader: the screen's pages and the party blocks' type-ahead.
  // `invalidateAddressBook` sweeps both, since any write can change what either shows.
  addressBook: (query: AddressBookQuery = {}) => ['address-book', query] as const,
  addressBookSuggestions: (q: string, kind: AddressBookKind = 'PARTY') => ['address-book-suggestions', kind, q] as const,
}

/** Everything that a write to one document can have made stale. */
export function invalidateDeca(client: QueryClient, id?: string): void {
  void client.invalidateQueries({ queryKey: ['decas'] })
  void client.invalidateQueries({ queryKey: ['deca-stats'] })
  if (id) void client.invalidateQueries({ queryKey: ['deca', id] })
}

export function useInvalidateDeca() {
  const client = useQueryClient()
  return (id?: string) => invalidateDeca(client, id)
}

/**
 * Everything a write to one route sheet can have made stale — the invalidation
 * `usePassengerDecaList` below says the passenger writes would bring.
 *
 * The list prefix always, and the sheet's own keys when the caller knows which sheet
 * changed. There is deliberately no `deca-stats` counterpart: the contract's
 * `/passenger-decas/stats` exists but nothing in the portal reads it yet, and
 * invalidating a key no query holds is a confident lie about what this call does.
 *
 * Kept apart from `invalidateDeca` for the reason stated there — a write to one
 * document family has nothing to say about the other, and throwing away the goods
 * caches on a route-sheet write would be a confident lie about what changed.
 *
 * ⚠️ `id` is optional and stays optional: ~~`PassengerCreateForm` calls this before it
 * has an id to name.~~ **No longer true since #90 (2026-09-22):** that call moved below the
 * request, where `created.id` exists, because an *amended* draft has a cached detail screen to
 * invalidate and the create path never did. **Every caller now names a sheet** — the two here,
 * two in `PassengerDetailScreen` and three in `PassengerModifyDialog` — so the optionality is
 * unexercised and could be tightened to `string`.
 *
 * The warning it carried still stands, and is why the parameter is worth a note at all: a
 * sheet-scoped write that forgets to pass one leaves the detail screen showing the document as
 * it was before the write — which on a legal record is the failure worth naming here.
 */
export function invalidatePassengerDeca(client: QueryClient, id?: string): void {
  void client.invalidateQueries({ queryKey: ['passenger-decas'] })
  if (id) void client.invalidateQueries({ queryKey: ['passenger-deca', id] })
}

export function useInvalidatePassengerDeca() {
  const client = useQueryClient()
  return (id?: string) => invalidatePassengerDeca(client, id)
}

export function useDecaList(query: DecaListQuery, enabled = true) {
  return useQuery({
    queryKey: qk.decas(query),
    enabled,
    queryFn: () => unwrap(api.GET('/api/v1/decas', { params: { query } })),
  })
}

/**
 * A page of passenger route sheets — the second source of the merged list (design D1).
 *
 * Its own key prefix rather than a branch of `['decas']`, and deliberately outside
 * `invalidateDeca`: a route sheet is a different legal instrument with its own lifecycle, so a
 * write to a goods DeCA has nothing to say about one. The passenger writes are their own task and
 * will bring their own invalidation.
 *
 * `enabled` is the whole reason the argument exists here: filtering the list to *Mercancías* must
 * cost no second request, and a filter the passenger endpoint cannot express must leave this
 * source out rather than answer the wrong question.
 */
export function usePassengerDecaList(query: PassengerDecaListQuery, enabled = true) {
  return useQuery({
    queryKey: qk.passengerDecas(query),
    enabled,
    queryFn: () => unwrap(api.GET('/api/v1/passenger-decas', { params: { query } })),
  })
}

export function useDeca(id: string | undefined) {
  return useQuery({
    queryKey: qk.deca(id ?? ''),
    enabled: Boolean(id),
    queryFn: () =>
      unwrapWithETag(api.GET('/api/v1/decas/{decaId}', { params: { path: { decaId: id! } } })),
  })
}

/**
 * The selected documents, read one by one.
 *
 * Not a list page: the list is paginated and filtered, so a selection made across pages is not
 * a subset of any single page — which is how an id could reach `POST /decas/group` without ever
 * appearing in the review table or the party pre-check that reads from this hook. `documentIds`
 * caps at 50 on the contract, so this bounded fan-out of single reads is the honest shape; no
 * endpoint accepts a set of ids.
 *
 * Same key and fetcher as `useDeca` — `qk.deca(id)` and `GET /decas/{decaId}` — on purpose:
 * `useInvalidateDeca` invalidates the `['deca', id]` prefix per id, and a second key or fetcher
 * for the same resource would mean that invalidation stops reaching whichever half was hand-rolled.
 */
export function useDecasByIds(ids: readonly string[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: qk.deca(id),
      queryFn: () =>
        unwrapWithETag(api.GET('/api/v1/decas/{decaId}', { params: { path: { decaId: id } } })),
    })),
  })
}

/**
 * Counts, optionally scoped to a date window.
 *
 * The window is what makes the Panel's month KPIs and their trend possible
 * without a contract change: `/decas/stats` takes `fechaDesde` / `fechaHasta`,
 * bounding when a document was created (the list bounds the service date under
 * the same names), so "this month" and "last month" are two calls, and the
 * comparison is arithmetic rather than a field the API would have to grow.
 * Each window is its own cache entry; `invalidateDeca` still clears them
 * all, because it invalidates on the `deca-stats` prefix.
 */
export function useDecaStats(query: DecaStatsQuery = {}) {
  return useQuery({
    queryKey: qk.decaStats(query),
    queryFn: () => unwrap(api.GET('/api/v1/decas/stats', { params: { query } })),
  })
}

/**
 * One route sheet, with its `ETag`.
 *
 * `unwrapWithETag` for the reason the goods read uses it: every passenger operation that
 * mutates an existing sheet declares `If-Match` **required**, and `GET /passenger-decas/{sheetId}`
 * is the only place an `ETag` is handed out. Without the read carrying its version into the
 * write, two users amending the same sheet silently overwrite one another.
 */
export function usePassengerDeca(id: string | undefined) {
  return useQuery({
    queryKey: qk.passengerDeca(id ?? ''),
    enabled: Boolean(id),
    queryFn: () =>
      unwrapWithETag(
        api.GET('/api/v1/passenger-decas/{sheetId}', { params: { path: { sheetId: id! } } }),
      ),
  })
}

export function usePassengerDecaHistory(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.passengerDecaHistory(id ?? ''),
    enabled: Boolean(id) && enabled,
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/passenger-decas/{sheetId}/history', {
          params: { path: { sheetId: id! } },
        }),
      ),
  })
}

export function usePassengerDecaVersions(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.passengerDecaVersions(id ?? ''),
    enabled: Boolean(id) && enabled,
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/passenger-decas/{sheetId}/versions', {
          params: { path: { sheetId: id! } },
        }),
      ),
  })
}

export function useDecaHistory(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.decaHistory(id ?? ''),
    enabled: Boolean(id) && enabled,
    queryFn: () =>
      unwrap(api.GET('/api/v1/decas/{decaId}/history', { params: { path: { decaId: id! } } })),
  })
}

export function useDecaVersions(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.decaVersions(id ?? ''),
    enabled: Boolean(id) && enabled,
    queryFn: () =>
      unwrap(api.GET('/api/v1/decas/{decaId}/versions', { params: { path: { decaId: id! } } })),
  })
}

/**
 * A goods document's delivery confirmations (#160), oldest first. `enabled` is the document's own
 * `delivery`: before the first confirmation there is nothing to ask for.
 */
export function useDeliveryConfirmations(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.deliveryConfirmations(id ?? ''),
    enabled: Boolean(id) && enabled,
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/decas/{decaId}/delivery-confirmations', { params: { path: { decaId: id! } } }),
      ),
  })
}

/**
 * A DeCA's sendings by email (#219), newest first. While a letter is on its way the list is read
 * again every few seconds, as the users list reads its letters (`mailPollInterval`).
 */
export function useDecaEmails(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.decaEmails(id ?? ''),
    enabled: Boolean(id) && enabled,
    queryFn: () => unwrap(api.GET('/api/v1/decas/{decaId}/emails', { params: { path: { decaId: id! } } })),
    refetchInterval: (query) => decaEmailPollInterval(query.state.data?.data ?? [], Date.now()),
  })
}

/**
 * Which of [addresses] take no mail (#141): a map from each such address, in lower case, to why.
 * Only addresses the server would take are asked about, in one question per set.
 */
export function useDeadAddresses(addresses: readonly string[]): ReadonlyMap<string, string> {
  const asked = [...new Set(addresses.filter(isEmailAddress).map((address) => address.toLowerCase()))].sort()
  const query = useQuery({
    queryKey: qk.emailAddressCheck(asked),
    enabled: asked.length > 0,
    staleTime: 60_000,
    queryFn: () => unwrap(api.POST('/api/v1/email-addresses/check', { body: { addresses: asked } })),
  })
  const dead = new Map<string, string>()
  for (const answer of query.data?.data ?? []) {
    if (answer.dead && answer.reason) dead.set(answer.address.toLowerCase(), answer.reason)
  }
  return dead
}

/**
 * One photograph of a delivery confirmation, as bytes. An administrator's only (design D5); the
 * caller decides who asks.
 *
 * Kept for the session (design D8): never stale and never collected, so the thumbnail, the full-size
 * dialog, «Descargar» and a return to the document all use one read — each read is a
 * `deliveryConfirmation.photo.view` row in the audit trail. "The session" ends wherever the whole
 * cache is emptied, and the photographs are read again after it: when the signed-in identity changes
 * (`sessionCache.ts`), and whenever a platform role chooses another company in the header or leaves
 * it, or opens a row of the cross-company list that belongs to another company
 * (`useOrganisationChoice`; choosing the company already chosen ends nothing).
 */
export function useDeliveryPhoto(decaId: string, confirmationId: string, n: number) {
  return useQuery({
    queryKey: qk.deliveryPhoto(decaId, confirmationId, n),
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: () =>
      fetchBinary(
        `/api/v1/decas/${encodeURIComponent(decaId)}/delivery-confirmations/${encodeURIComponent(confirmationId)}/photos/${n}`,
        MIME.jpeg,
      ),
  })
}

export function useImports() {
  return useQuery({
    queryKey: qk.imports(),
    queryFn: () => unwrap(api.GET('/api/v1/imports', { params: { query: { perPage: 10 } } })),
  })
}

/**
 * Import validation and commit both run as background jobs, so the job is polled
 * while it is working and left alone once it settles.
 */
export function useImportJob(id: string | undefined) {
  return useQuery({
    queryKey: qk.import(id ?? ''),
    enabled: Boolean(id),
    queryFn: () =>
      unwrap(api.GET('/api/v1/imports/{importId}', { params: { path: { importId: id! } } })),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'UPLOADED' || status === 'VALIDATING' || status === 'COMMITTING'
        ? 1500
        : false
    },
  })
}

export function useImportRows(id: string | undefined, status?: ImportRowStatus, enabled = true) {
  return useQuery({
    queryKey: qk.importRows(id ?? '', status),
    enabled: Boolean(id) && enabled,
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/imports/{importId}/rows', {
          params: { path: { importId: id! }, query: { perPage: 200, ...(status ? { status } : {}) } },
        }),
      ),
  })
}

/**
 * The client companies, one page of them. `keepPrevious` holds the last answer on screen while the
 * next search is in flight, for the top bar's company selector, whose rows would otherwise vanish
 * and come back under the user on every pause in typing. The administration list does not pass it.
 */
export function useOrganisations(query: OrganisationQuery, enabled = true, options: { keepPrevious?: boolean } = {}) {
  return useQuery({
    queryKey: qk.organisations(query),
    enabled,
    ...(options.keepPrevious ? { placeholderData: keepPreviousData } : {}),
    queryFn: () => unwrap(api.GET('/api/v1/organisations', { params: { query } })),
  })
}

/**
 * One organisation, for the settings the reduced `principal.organisation` does not carry.
 *
 * `CurrentPrincipal.organisation` is `{ id, legalName, nif?, isPlatform? }` in the frozen contract —
 * `settings.locale` lives only on the full `Organisation` schema, and the generated client cannot be
 * widened by hand because CI fails on drift against the YAML.
 */
export function useOrganisation(id: string | undefined) {
  return useQuery({
    queryKey: qk.organisation(id ?? ''),
    enabled: Boolean(id),
    queryFn: () =>
      unwrap(api.GET('/api/v1/organisations/{orgId}', { params: { path: { orgId: id! } } })),
  })
}

/**
 * `refetchInterval` is handed the page it last fetched and answers how soon to ask again — the
 * Usuarios screen polls while a letter is on its way (#114, `deca/emailDelivery.ts`).
 */
export function useUsers(
  query: UserQuery,
  enabled = true,
  refetchInterval?: (users: readonly User[]) => number | false,
) {
  return useQuery({
    queryKey: qk.users(query),
    enabled,
    queryFn: () => unwrap(api.GET('/api/v1/users', { params: { query } })),
    ...(refetchInterval
      ? { refetchInterval: (q: { state: { data?: { data: User[] } } }) => refetchInterval(q.state.data?.data ?? []) }
      : {}),
  })
}

export function useApiKeys(enabled = true) {
  return useQuery({
    queryKey: qk.apiKeys(),
    enabled,
    queryFn: () => unwrap(api.GET('/api/v1/api-keys', {})),
  })
}

/** ASGTRANS's service accounts (#161): a platform super administrator's, read as ASGTRANS itself. */
export function useServiceAccounts(enabled = true) {
  return useQuery({
    queryKey: qk.serviceAccounts(),
    enabled,
    queryFn: () => unwrap(api.GET('/api/v1/service-accounts', {})),
  })
}

/**
 * How often the *Empresa* tab re-reads its two lists while a code is waiting to be typed (#110).
 *
 * A driver redeeming a code moves it from one list to the other on the server, and with
 * `staleTime` 30 s and no refetch on focus (`main.tsx`) the office would otherwise never see the
 * phone arrive. Only while something is pending: an idle tab asks nothing.
 */
export const PHONE_POLL_MS = 10_000

/** The codes still waiting for a driver. Polls itself while it lists any. */
export function usePhoneLinkCodes(orgId: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.phoneLinkCodes(orgId),
    enabled,
    refetchInterval: (query) => ((query.state.data?.data.length ?? 0) > 0 ? PHONE_POLL_MS : false),
    queryFn: () =>
      unwrap(api.GET('/api/v1/organisations/{orgId}/phone-link-codes', { params: { path: { orgId } } })),
  })
}

/** The phones linked to the company. `poll` is the codes list's answer: is a code pending? */
export function usePhoneLinks(orgId: string, enabled: boolean, poll: boolean) {
  return useQuery({
    queryKey: qk.phoneLinks(orgId),
    enabled,
    refetchInterval: poll ? PHONE_POLL_MS : false,
    queryFn: () =>
      unwrap(api.GET('/api/v1/organisations/{orgId}/phone-links', { params: { path: { orgId } } })),
  })
}

/** After any act on a phone or a code — a code typed meanwhile changes both lists at once. */
export function invalidatePhoneLinks(client: QueryClient, orgId: string): void {
  void client.invalidateQueries({ queryKey: qk.phoneLinks(orgId) })
  void client.invalidateQueries({ queryKey: qk.phoneLinkCodes(orgId) })
}

/**
 * The external DeCAs this company has recorded.
 *
 * Kept off `invalidateDeca` on purpose: these are not our documents, they carry no estado and
 * they are counted by no KPI, so a write to one of ours has nothing to say about them — and a
 * write to one of these must not throw away the document caches either.
 */
export function useExternalDecas(query: ExternalDecaQuery, enabled = true) {
  return useQuery({
    queryKey: qk.externalDecas(query),
    enabled,
    queryFn: () => unwrap(api.GET('/api/v1/external-decas', { params: { query } })),
  })
}

export function useExternalDeca(id: string | undefined) {
  return useQuery({
    queryKey: qk.externalDeca(id ?? ''),
    enabled: Boolean(id),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/external-decas/{externalDecaId}', {
          params: { path: { externalDecaId: id! } },
        }),
      ),
  })
}

/**
 * After any write to the address book — an entry saved, corrected or removed, a suggestion hidden
 * or shown again. Both readers, because a write changes both: a saved entry wins over the documents
 * for its NIF in the list **and** in the suggestions, and a hidden NIF leaves both.
 */
export function invalidateAddressBook(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: ['address-book'] })
  void client.invalidateQueries({ queryKey: ['address-book-suggestions'] })
}

/**
 * A page of the company's address book (#159), or with `hidden: true` the NIFs whose suggestion
 * was hidden.
 *
 * Not swept by `invalidateDeca`. The server builds the book from the documents on every read, so a
 * document issued meanwhile appears in it on the next fetch, at most `staleTime` (`main.tsx`) later;
 * no screen shows a document and the book side by side for the gap to be seen.
 */
export function useAddressBook(query: AddressBookQuery, enabled = true) {
  return useQuery({
    queryKey: qk.addressBook(query),
    enabled,
    queryFn: () => unwrap(api.GET('/api/v1/address-book', { params: { query } })),
  })
}

/**
 * The entries of one kind matching what is typed in a field, at most ten: a party's by default.
 *
 * Nothing is asked under `SUGGESTION_MIN_LENGTH` characters once trimmed: the server would answer
 * an empty list anyway, and a field that asks on every first letter asks for nothing useful.
 * `keepPreviousData` holds the last answer on screen while the next search is in flight, so the
 * list does not close and reopen under the user on every letter they type. The caller decides
 * whether it is shown at all.
 */
export function useAddressBookSuggestions(q: string, enabled = true, kind: AddressBookKind = 'PARTY') {
  const term = q.trim()
  return useQuery({
    queryKey: qk.addressBookSuggestions(term, kind),
    enabled: enabled && term.length >= SUGGESTION_MIN_LENGTH,
    placeholderData: keepPreviousData,
    queryFn: () =>
      // A party is what the server reads with no kind, so a party's request stays #159's.
      unwrap(
        api.GET('/api/v1/address-book/suggestions', {
          params: { query: kind === 'PARTY' ? { q: term } : { q: term, kind } },
        }),
      ),
  })
}

export function useAuditEvents(query: AuditQuery, enabled = true) {
  return useQuery({
    queryKey: qk.audit(query),
    enabled,
    queryFn: () => unwrap(api.GET('/api/v1/audit-events', { params: { query } })),
  })
}
